<template>
  <div class="income-page" :aria-busy="loading">
    <header class="report-header">
      <div><p class="eyebrow">LAPORAN KEUANGAN</p><h2>Laporan Laba Rugi</h2><p>{{ company.name || 'Perusahaan' }} &middot; Tahun buku {{ selectedYear }}</p></div>
      <vs-button :disabled="!canExport" @click="printReport">{{ exporting ? 'Menyiapkan PDF...' : 'Unduh PDF' }}</vs-button>
    </header>
    <form class="filter-row" @submit.prevent="load">
      <div><label for="income-start">Tanggal awal</label><input id="income-start" v-model="startDate" type="date" :disabled="loading" required /></div>
      <div><label for="income-end">Tanggal akhir</label><input id="income-end" v-model="endDate" type="date" :disabled="loading" required /></div>
      <button class="apply-button" type="submit" :disabled="loading">{{ loading ? 'Memuat laporan...' : 'Tampilkan laporan' }}</button>
    </form>
    <p v-if="dirty && ready && !error" class="filter-note" role="status">Periode diubah. Klik Tampilkan laporan sebelum mengunduh PDF.</p>
    <section v-if="loading" class="report-state" role="status">Menghitung laporan dari jurnal yang sudah diposting...</section>
    <section v-else-if="error" class="report-state state-warning" role="alert">
      <h3>{{ configurationIssue ? 'Pengaturan laporan perlu dilengkapi' : 'Laporan belum dapat ditampilkan' }}</h3>
      <p>{{ error }}</p>
      <div v-if="unmappedAccounts.length" class="missing-accounts">
        <table><caption>Akun yang belum masuk bagian Laba Rugi ({{ unmappedAccounts.length }})</caption><thead><tr><th scope="col">Kode akun</th><th scope="col">Nama akun</th></tr></thead><tbody><tr v-for="account in unmappedAccounts" :key="account.account_id"><td>{{ account.account_code }}</td><td>{{ account.name }}</td></tr></tbody></table>
      </div>
      <div class="state-actions"><vs-button v-if="configurationIssue && canConfigure" to="/report-settings">Buka pengaturan laporan</vs-button><vs-button type="border" @click="load">Coba lagi</vs-button></div>
      <p v-if="configurationIssue && !canConfigure">Hubungi admin perusahaan untuk melengkapi pengaturan laporan.</p>
    </section>
    <template v-else-if="ready">
      <p class="period-note">Periode {{ periodLabel }} &middot; Rupiah (IDR) &middot; Jurnal POSTED</p>
      <div class="summary-grid">
        <div v-for="section in summarySections" :key="section.section_code" class="summary-item" :class="{ final: section.section_code === 'NET_PROFIT_LOSS' }"><span>{{ section.name }}</span><strong>{{ currency(section.amount) }}</strong></div>
      </div>
      <div class="report-list">
        <section v-for="section in sections" :key="section.section_code" class="report-section">
          <div class="section-head"><h4>{{ section.name }}</h4><strong>{{ currency(section.amount) }}</strong></div>
          <div v-if="section.accounts && section.accounts.length" class="account-lines"><div v-for="account in section.accounts" :key="account.account_uid || account.account_id" class="account-line"><span>{{ account.account_code }} &mdash; {{ account.name }}</span><span>{{ currency(account.amount) }}</span></div></div>
          <p v-else-if="section.section_type === 'ACCOUNT_GROUP'" class="empty-note">Belum ada akun pada bagian ini.</p>
        </section>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import moduleReport from '@/store/report/moduleReport.js'
export default {
  data () {
    const year = Number(localStorage.getItem('selectedYear')) || new Date().getFullYear()
    return { startDate: `${year}-01-01`, endDate: `${year}-12-31`, appliedPeriod: null, calculation: {}, sections: [], loading: false, exporting: false, error: '', configurationIssue: false, requestId: 0 }
  },
  computed: {
    company () { return JSON.parse(localStorage.getItem('activeCompany') || '{}') },
    selectedYear () { return localStorage.getItem('selectedYear') || new Date().getFullYear() },
    ready () { return !!this.calculation.ready },
    dirty () { return !this.appliedPeriod || this.startDate !== this.appliedPeriod.startDate || this.endDate !== this.appliedPeriod.endDate },
    canExport () { return this.ready && !this.dirty && !this.loading && !this.exporting && !this.error },
    canConfigure () { const user = JSON.parse(localStorage.getItem('userInfo') || '{}'); return user.role_id !== null && user.role_id !== undefined && [0, 1].includes(Number(user.role_id)) },
    unmappedAccounts () { return this.calculation.unmapped_accounts || [] },
    periodLabel () { return this.appliedPeriod ? `${moment(this.appliedPeriod.startDate).format('DD MMM YYYY')} sampai ${moment(this.appliedPeriod.endDate).format('DD MMM YYYY')}` : '' },
    summarySections () { return this.sections.filter(s => ['GROSS_PROFIT', 'OPERATING_PROFIT', 'OTHER_NET', 'NET_PROFIT_LOSS'].includes(s.section_code)) }
  },
  created () { if (!moduleReport.isRegistered) { this.$store.registerModule('report', moduleReport); moduleReport.isRegistered = true } this.load() },
  beforeDestroy () { this.requestId++ },
  methods: {
    async load () {
      const requestId = ++this.requestId
      const start = moment(this.startDate, 'YYYY-MM-DD', true)
      const end = moment(this.endDate, 'YYYY-MM-DD', true)
      this.error = ''
      this.configurationIssue = false
      this.calculation = {}
      this.sections = []
      if (!start.isValid() || !end.isValid() || start.isAfter(end) || start.year() !== end.year() || start.year() !== Number(this.selectedYear)) {
        this.error = 'Pilih tanggal awal dan akhir yang berurutan dalam tahun buku aktif.'
        this.loading = false
        return
      }
      const period = { startDate: this.startDate, endDate: this.endDate }
      this.loading = true
      try {
        const response = await this.$store.dispatch('report/fetchLabaRugi', period)
        if (requestId !== this.requestId) return
        this.calculation = response.data.calculation || {}
        if (!this.calculation.ready) {
          this.error = this.calculation.reason || 'Lengkapi pengaturan laporan untuk melanjutkan.'
          this.configurationIssue = true
          return
        }
        this.sections = this.calculation.sections || []
        this.appliedPeriod = period
      } catch (error) {
        if (requestId !== this.requestId) return
        const response = error && error.response
        const data = response && response.data
        this.calculation = (data && data.calculation) || {}
        this.configurationIssue = !!(response && response.status === 409)
        this.error = (data && data.message) || 'Koneksi gagal. Periksa jaringan lalu coba lagi.'
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    async printReport () {
      if (!this.canExport) return
      this.exporting = true
      const period = this.appliedPeriod
      try {
        await this.$store.dispatch('report/printReportLabaRugi', { company_id: this.company.id, start_date: period.startDate, end_date: period.endDate })
        this.$vs.notify({ color: 'success', title: 'PDF siap', text: 'Laporan berhasil diunduh.' })
      } catch (error) {
        this.$vs.notify({ color: 'danger', title: 'Unduhan gagal', text: error.message || 'PDF tidak dapat dibuat. Coba lagi.' })
      } finally { this.exporting = false }
    },
    currency (value) { return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(value) || 0) }
  }
}
</script>

<style scoped>
.income-page { color: #172033; max-width: 1440px; margin: 0 auto; }
.report-header { display: flex; align-items: center; justify-content: space-between; gap: 20px; margin-bottom: 24px; }
.report-header h2 { margin: 6px 0 8px; }
.report-header p, .period-note, .empty-note { color: #5d687a; }
.eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 1.4px; }
.filter-row { display: flex; align-items: flex-end; flex-wrap: wrap; gap: 16px; background: #fff; border: 1px solid #e2e6ed; border-radius: 10px; padding: 20px; margin-bottom: 16px; }
.filter-row label { display: block; font-weight: 600; font-size: 13px; margin-bottom: 8px; }
.filter-row input { border: 1px solid #aeb8c7; border-radius: 6px; padding: 10px 12px; min-height: 42px; color: #172033; }
.apply-button { background: rgba(var(--vs-primary), 1); color: #fff; border: 0; border-radius: 6px; padding: 12px 18px; cursor: pointer; }
.apply-button:disabled { opacity: .6; cursor: wait; }
input:focus-visible, button:focus-visible { outline: 3px solid var(--app-primary); outline-offset: 3px; }
.period-note, .filter-note { margin: 16px 0; font-size: 13px; }
.filter-note { color: var(--app-secondary-ink); }
.summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 16px 0 24px; }
.summary-item { background: #fff; border: 1px solid #e2e6ed; border-radius: 10px; padding: 20px; display: flex; flex-direction: column; gap: 12px; }
.summary-item strong { font-size: 20px; font-variant-numeric: tabular-nums; }
.summary-item.final { border-top: 3px solid rgba(var(--vs-primary), 1); }
.report-list { background: #fff; border: 1px solid #e2e6ed; border-radius: 10px; overflow: hidden; }
.report-section { padding: 20px; border-bottom: 1px solid #e2e6ed; }
.section-head, .account-line { display: flex; justify-content: space-between; gap: 20px; }
.section-head strong, .account-line span:last-child { white-space: nowrap; font-variant-numeric: tabular-nums; }
.account-lines { margin-top: 14px; }
.account-line { padding: 10px 0; border-top: 1px solid #f0f2f5; font-size: 13px; }
.empty-note { margin: 10px 0 0; font-size: 13px; }
.report-state { background: #fff; border: 1px solid #e2e6ed; border-radius: 10px; padding: 28px; }
.state-warning { border-left: 4px solid var(--app-secondary-ink); }
.report-state p { margin: 12px 0; line-height: 1.6; }
.state-actions { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 20px; }
.missing-accounts { max-height: 320px; overflow: auto; margin-top: 16px; }
.missing-accounts table { width: 100%; border-collapse: collapse; text-align: left; }
.missing-accounts caption { text-align: left; margin-bottom: 12px; font-weight: 600; }
.missing-accounts th, .missing-accounts td { padding: 10px; border-bottom: 1px solid #e2e6ed; }
@media(max-width: 900px) { .summary-grid { grid-template-columns: 1fr 1fr; } }
@media(max-width: 600px) { .report-header { align-items: stretch; flex-direction: column; } .summary-grid { grid-template-columns: 1fr; } .filter-row > div, .filter-row input { width: 100%; } .section-head, .account-line { flex-wrap: wrap; gap: 8px; } }
</style>
